import type { TamsClient, TamsSegment } from "@mimir/common/types/tams";
import { CmafSegmentSource, CmafSegmentIterator, CmafSegment } from "./mseBuffering";
import { CodecString } from "@mimir/common/dto/mediaAvailability";
import { addFuzzy, fractionToFuzzy, humanReadableRange, integerToFuzzy, maxFuzzy, subtractFuzzy } from "@mimir/common/util/fuzzyMath";
import { Logger } from "@mimir/common/types/logger";
import { parseTamsTimerange, serializeTamsTimerange, tamsBinarySearchLastSegment } from "@mimir/common/tams/utils";
import { FuzzyBounds, FuzzyValue } from "@mimir/common/util/fuzzyNumbers";
import { BasicFetch } from "@mimir/common/types/basicFetch";
import { AsyncTransmuxer, AsyncTransmuxerProvider } from "./asyncTransmuxer";
import { readWholeStream } from "@mimir/common/util/readableStream";
import { isAbortError } from "@mimir/common/util/abortControl";
import { Lock } from "@mimir/common/async/lock";
import { fetchOrThrow } from "@mimir/common/io/fetchOrThrow";
import { asyncTimeout } from "@mimir/common/async/asyncTimeout";
import { PrefixedLogger } from "@mimir/common/log/prefixedLogger";

export function createTamsCmafSegmentSource(
  services: {
    asyncTransmuxerProvider: AsyncTransmuxerProvider,
    fetch: BasicFetch,
    logger: Logger,
    tamsClient: TamsClient
  },
  playerDebugId: string,
  flowId: string,
): CmafSegmentSource {
  const logger = new PrefixedLogger(services.logger, `[${playerDebugId}:tams:${flowId}]`);
  return new TamsCmafSegmentSource({ ...services, logger }, playerDebugId, flowId);
}

// End of public api

class TamsCmafSegmentSource implements CmafSegmentSource {
  private flowInfo : TamsFlowInfo | null = null;
  private cachedFlowInfoLock : Lock = new Lock();
  
  private firstSegment : Uint8Array | null = null;
  private firstSegmentLock : Lock = new Lock();
  private iteratorCounter : number = 0;

  public constructor(
    private services: {
      asyncTransmuxerProvider: AsyncTransmuxerProvider,
      fetch: BasicFetch,
      logger: Logger,
      tamsClient: TamsClient
    },
    private playerDebugId: string,
    private flowId: string,
  ) {

  }

  public humanReadableDescription(): string {
    return `tams:${this.flowId}`;
  }

  public async codec(): Promise<string> {
    const flowInfo = await this.getCachedTamsFlowInfo();
    return flowInfo.codec;
  }

  public async createIterator(
    startTime: FuzzyValue | null,
    _invalidateBuffer: () => void,
  ): Promise<CmafSegmentIterator> {
    const firstSegment = await this.getCachedFirstSegment();
    const flowInfo = await this.getCachedTamsFlowInfo();

    const requestedStartTime =
      maxFuzzy(startTime ?? flowInfo.range.start, flowInfo.range.start);

    let iteratorStartTime = requestedStartTime;

    // The CMAF iterator need to report its start position meaning that we need
    // to query the actual segment range at the requested start time.
    
    const timerange = serializeTamsTimerange({ start: requestedStartTime, end: requestedStartTime }, 'inclusive', 'inclusive');
    const [tamsSegment] = await this.services.tamsClient.getSegments({ flowId: this.flowId, timerange });
    if (tamsSegment !== undefined) {
      iteratorStartTime = parseTamsTimerange(tamsSegment.timerange).start;
    }

    const logger = new PrefixedLogger(this.services.logger, `[${this.iteratorCounter}]`);
    this.iteratorCounter += 1;

    // TODO: Potential optimization here is to pass the TamsSegment in there
    // so that the iterator does not have to re-request this TamsSegment on
    // the first next() call. This may speed up seeking with tams.
    return new TamsCmafSegmentIterator(
      {
        ...this.services,
        logger,
      },
      this.playerDebugId,
      this.flowId,
      iteratorStartTime,
      firstSegment,
    );
  }

  private async getCachedFirstSegment(): Promise<Uint8Array> {
    try {
      await this.firstSegmentLock.waitForLock();
      if (this.firstSegment === null) {
        const { tamsClient } = this.services;
        const [segment] = await tamsClient.getSegments({ flowId: this.flowId, limit: 1 });
        if (segment === undefined) {
          throw new Error('Unable to obtain first segment from TAMS');
        }
        this.firstSegment = await fetchTamsSegment(this.services, segment);
      }
      return this.firstSegment;
    } finally {
      this.firstSegmentLock.releaseLock();
    }
  }

  public async duration(): Promise<FuzzyValue> {
    const flowInfo = await this.getCachedTamsFlowInfo();
    return subtractFuzzy(flowInfo.range.end, flowInfo.range.start);
  }

  // end of public API

  private async getCachedTamsFlowInfo(): Promise<TamsFlowInfo> {
    try {
      await this.cachedFlowInfoLock.waitForLock()
      if (this.flowInfo === null) {
        this.flowInfo = await this.getTamsFlowInfo();
      }
      return this.flowInfo;
    } finally {
      this.cachedFlowInfoLock.releaseLock();
    }
  }

  private async getTamsFlowInfo(): Promise<TamsFlowInfo> {
    const { logger, tamsClient } = this.services;
    const flow = await tamsClient.getFlow(this.flowId);
    if (flow === null) {
      throw new Error(`No such flow: ${this.flowId}`);
    }

    const codec = getCodecStringFromTamsCodec(flow.codec);

    let range : FuzzyBounds;
    if (flow.timerange === undefined) {
      range =
        await findSegmentRangeFromFlow(
          logger,
          tamsClient,
          this.flowId,
          this.flowInfo?.range ?? null,
        );
    } else {
      range = parseTamsTimerange(flow.timerange);
    }
    
    return { range, codec };
  }
}

async function findSegmentRangeFromFlow(
  logger: Logger,
  tamsClient: TamsClient,
  flowId: string,
  lastKnownRange: FuzzyBounds | null,
) : Promise<FuzzyBounds> {
  let start : FuzzyValue;
  if (lastKnownRange !== null) {
    start = lastKnownRange.start;
  } else {
    const [firstSegment] = await tamsClient.getSegments({ flowId, limit: 1 });

    if (firstSegment === undefined) {
      logger.warn('Flow has no segments. Setting duration to 0');
      return { start: integerToFuzzy(0), end: integerToFuzzy(0) };
    }
    start = parseTamsTimerange(firstSegment.timerange).start;
  }

  const lastSegment =
    await tamsBinarySearchLastSegment(
      logger,
      tamsClient,
      flowId,
      lastKnownRange?.start ?? null,
      false,
    );

  if (lastSegment === null) {
    logger.warn('Unable to find last segment via binary search.');
    return { start: integerToFuzzy(0), end: integerToFuzzy(0) };
  }

  const end = parseTamsTimerange(lastSegment.timerange).end;

  return { start, end };
}

interface TamsFlowInfo {
  codec: CodecString,
  range: FuzzyBounds,
}


class TamsCmafSegmentIterator implements CmafSegmentIterator {
  private currentPosition : FuzzyValue;
  private transmuxer : AsyncTransmuxer;
  private abortController : AbortController = new AbortController();
  private isInEofState : boolean = false;

  constructor(
    private services: {
      asyncTransmuxerProvider: AsyncTransmuxerProvider
      fetch: BasicFetch,
      logger: Logger,
      tamsClient: TamsClient,
    },
    private playerDebugId: string,
    private flowId: string,
    private _startPosition: FuzzyValue,
    private firstSegment: Uint8Array | null,
  ) {
    this.transmuxer = services.asyncTransmuxerProvider();
    this.currentPosition = _startPosition;
  }

  async next(bufferedRanges: FuzzyBounds[]): Promise<CmafSegment | 'closed'> {
    try {
      while (true) {
        const result = await this.nextInternal(bufferedRanges);
        if (result === 'retry') {
          await asyncTimeout(1000);
          continue;
        }
        if (result === 'skip') {
          continue;
        }
        return result;
      }
    } catch (e) {
      if (isAbortError(e)) {
        return 'closed';
      }
      throw e;
    }
  }

  async nextInternal(_bufferedRanges: FuzzyBounds[]): Promise<CmafSegment | 'retry' | 'skip'> {
    const { logger } = this.services;
    // TODO: Use bufferedRanges to potentially skip segments.
    const signal = this.abortController.signal;

    signal.throwIfAborted();

    if (this.firstSegment) {
      const output =
        await this.transmuxer(
          this.playerDebugId,
          this.firstSegment, 
          null,
          true,
        );

      signal.throwIfAborted();

      this.firstSegment = null;

      logger.info('TAMS iterator returning initialization segment');

      return {
        bytes: output.initialization,
        timeRange: null,
      };
    };

    // The current position is always the "end" value of the previous segment.
    // If we ask to query this position at the exact segment boundary we are
    // asking for trouble. Assuming no segment will ever be smaller than 100ms
    // let's add the offset.
    const epsilon = fractionToFuzzy({ numerator: 1, denominator: 10 });
    const positionWithOffset = addFuzzy(this.currentPosition, epsilon);

    logger.info(`TAMS iterator looking for segment at position: ${positionWithOffset.approximate.toFixed(2)}`);
    const queryRange = { start: positionWithOffset, end: positionWithOffset };
    const timerange = serializeTamsTimerange(queryRange, 'inclusive', 'inclusive');

    const { tamsClient } = this.services;
    const flowId = this.flowId;

    const [tamsSegment] =
      await tamsClient.getSegments({ flowId, timerange, limit: 1 });

    if (tamsSegment === undefined) {
      if (!this.isInEofState) {
        logger.info(`Reached EOF at ${positionWithOffset.approximate.toFixed(2)}. Will retrying in case the stream grows`);
        this.isInEofState = true;
      }
      return 'retry';
    }

    if (this.isInEofState) {
      logger.info(`TAMS iterator has growing at position ${positionWithOffset.approximate.toFixed(2)}. Will resume providing segments.`);
      this.isInEofState = false;
    }

    const timeRange = parseTamsTimerange(tamsSegment.timerange);

    logger.info(`TAMS iterator got segment with range: ${humanReadableRange(timeRange)}`);

    const tsBytes = await fetchTamsSegment(this.services, tamsSegment);
    const { mediaSegment: bytes } = await this.transmuxer(this.playerDebugId, tsBytes, null, false);

    const cmafSegment : CmafSegment = { bytes, timeRange, };

    const segmentRange = parseTamsTimerange(tamsSegment.timerange);
    this.currentPosition = segmentRange.end;

    logger.info(`TAMS iterator returning segment and incrementing position to ${this.currentPosition.approximate.toFixed(0)}`);

    return cmafSegment;
  }

  startPosition(): FuzzyValue {
    return this._startPosition;
  }

  close(): void {
    this.abortController.abort();
  }
}

function getCodecStringFromTamsCodec(tamsCodec: string | undefined): CodecString {
  switch (tamsCodec) {
    case 'video/h264':
      return CodecString.MP4_VIDEO_H264_BASELINE_AVC_20;
    case 'audio/aac':
      return CodecString.MP4_AUDIO_AAC_LOW_COMPLEXITY;
    default:
      throw new Error(`Unknown TAMS codec string: ${tamsCodec}`);
  }
}

async function fetchTamsSegment(
  services: {
    fetch: BasicFetch,
    logger: Logger,
  },
  segment: TamsSegment
): Promise<Uint8Array> {
  const { fetch, logger } = services;

  // Prefer Presigned URL. Look for hints in the label.
  // TODO: We need a proper way to specify which URL to use.
  const preferredUrl =
    segment.get_urls?.find((entry) => entry.label.includes('presigned'));

  const url = (preferredUrl ?? segment.get_urls?.at(0))?.url;
  if (url === undefined) {
    throw new Error('TAMS segment has no URLs');
  }
  const response = await fetchOrThrow(logger, fetch, url, { });
  if (!response.ok) {
    throw new Error(`Unable to fetch TAMS media segment from ${url}`);
  }
  if (response.body === null) {
    throw new Error('response body for media segment is empty');
  }
  return await readWholeStream(response.body);
}

